/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ImSpinner8 } from "react-icons/im";
import { useNavigate, useParams } from "react-router-dom";

const appName = process.env.REACT_APP_NAME!;
const POLLING_INTERVAL = 15000;

export default function AccountConfirmationRedirect() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [supportEmail, setSupportEmail] = useState<string>('');
  const navigate = useNavigate();
  const { receiver_account_id } = useParams();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const initialize = async () => {
      document.title = "Account Verification";

      const appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
      if (appleTitle) appleTitle.setAttribute("content", appName);

      const element = document.querySelector('.relative.float-right.h-full.min-h-screen.w-full.bg-darkPrimary');
      if (element) {
        element.classList.replace('bg-darkPrimary', 'bg-white');
      }

      await fetchConfirmToken();

      // Start polling every 15 seconds
      intervalRef.current = setInterval(() => {
        fetchConfirmToken();
      }, POLLING_INTERVAL);
    };

    initialize();

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const fetchConfirmToken = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/receiveraccount/request`,
        { fn: "get-confirm-url", receiver_account_id: receiver_account_id }
      );

      if (response.data.status === 'ok') {
        const payload = response.data.payload;

        // Set logo and support email from response
        if (payload.logo_url) {
          setLogoUrl(payload.logo_url);
        }
        if (payload.support_email) {
          setSupportEmail(payload.support_email);
        }

        // Check if confirm URL is ready - redirect immediately without showing UI
        if (payload.has_confirm_url === 'yes' && payload.confirm_url) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          window.location.href = payload.confirm_url;
          return;
        }

        // Show the page only if no confirm URL yet
        setIsLoading(false);
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        navigateToNotFoundPage();
      }
    } catch (error) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      navigateToNotFoundPage();
    }
  };

  const navigateToNotFoundPage = async () => {
    navigate(`/404`);
  };

  // Show plain page until first API response or while redirecting
  if (isLoading) {
    return <div className="h-screen bg-white" />;
  }

  return (
    <div className="flex items-start justify-center h-screen pt-20 overflow-hidden">
      <div className="flex flex-col items-center p-5">
        <div className="bg-white p-10 min-w-[540px] w-full border border-gray-300 rounded-2xl text-center">
          <div className="flex justify-center mb-5">
            {logoUrl && <img src={logoUrl} alt="Logo" style={{ width: '229px' }} />}
          </div>
          <div className="text-center mb-5">
            <h1 className="text-2xl text-gray-800 font-bold m-0">Account Verification</h1>
            <p className="text-sm text-gray-700 mt-2">Please wait while we generate your account confirmation</p>
          </div>
          <div className="flex justify-center mt-6">
            <ImSpinner8 className="spinner text-3xl text-gray-800" />
          </div>
          <p className="text-sm text-gray-700 mt-6">
            If you need further assistance, please contact <a href={`mailto:${supportEmail}`} className="underline">{supportEmail}</a>.
          </p>
        </div>
      </div>
    </div>
  );
}